// Vuex module for the Xero push flows.
//
// Orders are selected in bulk on the /orders page (same pattern as bulk
// mark-as-paid) and pushed to Xero by order id:
//   - createOrderInvoices → draft ACCREC customer invoices
//   - createMakerBills     → draft ACCPAY maker purchase bills
// The backend enqueues one draft per order; a cron consumer creates them in
// Xero as DRAFT for finance review. fetchXeroLink backs the XeroLink component
// on Maker/Org/Order detail pages.

import { EVENTS, track, numeric } from "~/utils/analytics"
function authConfig() {
	return {
		headers: {
			accept: "application/json",
			"x-access-token-user": useCookie("auth_token").value ?? "",
			"content-type": "application/json",
		},
	}
}

const xeroStoreModule = {
	namespaced: true,
	state: () => ({
		createBillsLoading: false,
		lastCreateSummary: null, // { enqueued, conflicts, missing, results }
		createInvoicesLoading: false,
		lastInvoiceSummary: null,
	}),
	mutations: {
		SET_CREATE_BILLS_LOADING(state, payload) {
			state.createBillsLoading = payload
		},
		SET_LAST_CREATE_SUMMARY(state, payload) {
			state.lastCreateSummary = payload
		},
		SET_CREATE_INVOICES_LOADING(state, payload) {
			state.createInvoicesLoading = payload
		},
		SET_LAST_INVOICE_SUMMARY(state, payload) {
			state.lastInvoiceSummary = payload
		},
	},
	actions: {
		// Resolve the Xero record + deep link an entity is synced to (or null).
		// Used by the XeroLink component on Maker/Org/Order detail pages.
		async fetchXeroLink({ dispatch }, { entity, entityId, doc }) {
			try {
				const params = new URLSearchParams({ entity, entity_id: String(entityId) })
				if (doc) params.set("doc", doc)
				return await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/xero/link?${params.toString()}`,
					authConfig()
				)
			} catch (error: any) {
				dispatch("handleError", { error }, { root: true })
				return { xero_id: null }
			}
		},

		// Enqueue draft purchase bills for the selected order IDs. Optional
		// xeroOrgIds maps order id → Xero org/contact id (org-less orders only).
		async createMakerBills({ commit, dispatch }, { orderIds, xeroOrgIds }) {
			commit("SET_CREATE_BILLS_LOADING", true)
			try {
				const body: Record<string, any> = { orderIds }
				if (xeroOrgIds && Object.keys(xeroOrgIds).length) body.xeroOrgIds = xeroOrgIds
				const summary = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/xero/maker-bills`,
					{ method: "POST", body, ...authConfig() }
				)
				commit("SET_LAST_CREATE_SUMMARY", summary)
				// Enqueue only — the cron consumer performs the actual Xero push.
				// conflict/missing counts are the integration's failure rate.
				track(EVENTS.XERO_DOCUMENTS_QUEUED, {
					document_type: "maker_bill",
					order_count: Array.isArray(orderIds) ? orderIds.length : undefined,
					enqueued_count: numeric((summary as any)?.enqueued ?? (summary as any)?.queued),
					conflict_count: numeric((summary as any)?.conflicts),
					missing_count: numeric((summary as any)?.missing),
				})
				return summary
			} catch (error: any) {
				dispatch("handleError", { error }, { root: true })
				return { ok: false, error }
			} finally {
				commit("SET_CREATE_BILLS_LOADING", false)
			}
		},

		// Enqueue draft customer invoices for the selected order IDs.
		async createOrderInvoices({ commit, dispatch }, { orderIds }) {
			commit("SET_CREATE_INVOICES_LOADING", true)
			try {
				const summary = await $fetch(
					`${import.meta.env.VITE_AND_DINE_API}/api/users/xero/invoices`,
					{ method: "POST", body: { orderIds }, ...authConfig() }
				)
				commit("SET_LAST_INVOICE_SUMMARY", summary)
				track(EVENTS.XERO_DOCUMENTS_QUEUED, {
					document_type: "order_invoice",
					order_count: Array.isArray(orderIds) ? orderIds.length : undefined,
					enqueued_count: numeric((summary as any)?.enqueued ?? (summary as any)?.queued),
					conflict_count: numeric((summary as any)?.conflicts),
					missing_count: numeric((summary as any)?.missing),
				})
				return summary
			} catch (error: any) {
				dispatch("handleError", { error }, { root: true })
				return { ok: false, error }
			} finally {
				commit("SET_CREATE_INVOICES_LOADING", false)
			}
		},
	},
}

export default xeroStoreModule
